/**
 * Sign uploader — step 8b.
 *
 * 8a carried the vocabulary; 8b adds the preview the page promises, the DPI
 * badge, a Proof render, and DesignState as a URL.
 *
 * DIVISION OF LABOUR (unchanged, item 54/55): upload-widget.js is served from
 * api.stickerplease.com and is not ours to edit. It gates add-to-cart
 * (fail-closed) and writes properties[Design] and properties[Preview] itself.
 * NOTHING HERE RENDERS properties[Design] — an earlier input of that name would
 * shadow the widget's and ship an artwork-less line behind an open gate.
 *
 * TWO RULES THAT LOOK LIKE DETAILS AND ARE NOT:
 *
 *   1. DPI IS READ FROM THE ORIGINAL'S HEADER, NEVER FROM THE DRAWN IMAGE (§5b).
 *      The badge and the canvas answer different questions: the canvas shows the
 *      server's companion, which may be a downscale, while the badge must speak
 *      for the file that will actually be printed. Deriving DPI from the drawn
 *      image would understate it by the derive ratio and colour a fine file red.
 *      Only the first 64 KB is read — the original may be 107 MP and must never
 *      be decoded in a browser tab (D5).
 *
 *   2. ENRICHMENT NEVER CLOSES THE GATE. The gate opened when the widget wrote
 *      Design. Proof and DesignState uploads are best-effort: any failure logs,
 *      leaves that property empty, and leaves the button enabled. Design is the
 *      print file; Proof is evidence.
 *
 *      THE ONE STATED EXCEPTION, 8.3: a double-sided OUTLINED sign needs two
 *      print files, and a line carrying one of them is not orderable. So this
 *      file now holds the gate shut while `DesignBack` is missing — and only
 *      ever by RE-DISABLING a button the widget has already enabled. It never
 *      enables one, and it releases only a hold it placed itself, and only
 *      while `Design` is still present. A failed BACK upload therefore behaves
 *      the opposite way to a failed Proof: the button stays shut and says why.
 *      Everything else in this file keeps rule 2 exactly.
 *
 * TWO FACES (8.3). A double-sided arrow is two designs, not one printed twice:
 * the back die is the front die mirrored, so the same artwork is clipped at the
 * opposite end. Front is the WIDGET's upload and opens the gate; the back is
 * OURS, posted straight to gw_uploadFile through post() below — the same path
 * that already carries proof.jpg and design-state.json — because upload-widget.js
 * mounts once, writes one Design, and is not ours to edit (items 54/55). Two
 * faces appear only when `sides === 'double'` AND the spec has an outline, which
 * in step 12 is the arrow and nothing else. Rectangles never see the second slot.
 *
 * CORS: api.stickerplease.com/uploads/ sends no Access-Control-Allow-Origin
 * (verified 2026-09-07, re-verified 2026-09-19 — GET with an Origin header
 * returns 200 and no ACAO, OPTIONS returns 405), so a remote image drawn to a
 * canvas taints it and toBlob throws. The proof is therefore rendered from the
 * customer's LOCAL file when the browser can render it and it is at or under
 * the server's own 25 MP derive threshold; otherwise no proof is produced and
 * nothing is claimed. DesignState.canvas.proofSource records which path ran.
 *
 * ── 8.4 ─────────────────────────────────────────────────────────────────────
 *
 * FACE LABELS BY DIRECTION (item 96). Both slot labels carry the die they name
 * — "Front — points right" / "Back — points left" — in the app's exact words,
 * written from the resolved spec and re-written wherever the spec can change.
 *
 * BACK-SLOT-STYLE (item 97). The back slot is the widget's own picker shape
 * rather than a bare file input, and its `accept` and format hint are MIRRORED
 * from the widget instead of restated. The list it used to carry in Liquid was
 * eight extensions against the server's fifteen.
 *
 * WEB-PREVIEW-BACKEND-FALLBACK (item 98) — WHAT LANDED AND WHAT DID NOT.
 *
 *   The FRONT already had it and still does: `enrich` prefers the widget's
 *   `Preview` over the local file, so a TIFF, a PSD or any raster past the
 *   derive threshold has always drawn the server's rendering on the die. No
 *   code here changed for that; 8.4 adds the tests that say so.
 *
 *   The BACK is what 8.4 builds. `post()` used to resolve to a bare `file_url`
 *   and threw `preview_url` away, so the back slot showed nothing for exactly
 *   the files the front showed. It now resolves to `{ url, previewUrl }` and
 *   `renderBackPreview` falls through to the server's rendering.
 *
 *   THE PROOF DOES NOT FALL BACK, AND CANNOT FROM THIS SURFACE. A proof is
 *   pixels read back out of a canvas, and every route to a cross-origin image's
 *   pixels is shut: drawImage paints (which is why the previews above work) but
 *   getImageData and toBlob throw SecurityError, `crossOrigin="anonymous"`
 *   refuses to load at all, and fetch() is blocked. All four measured from the
 *   live page on 2026-09-19. One `Access-Control-Allow-Origin` on /uploads/
 *   opens all of them; until it exists, a face the browser cannot decode has no
 *   proof, and `proofSource: 'none'` says so rather than shipping a sheet with
 *   a blank face on it.
 *
 *   AND FOR PDF, AI AND EPS THERE IS NOTHING TO FALL BACK TO. `gw_uploadFile`
 *   returns `preview_url: null` for all three (probed 2026-09-19); it renders
 *   TIFF and PSD and derives large rasters, and that is the whole list. Those
 *   three keep today's honest "Preview not available for this format" line.
 *
 * ── 8.5 ─────────────────────────────────────────────────────────────────────
 *
 * PDF-PREVIEW-CLIENTSIDE (item 117). A PDF or a PDF-compatible AI now has page
 * 1 rasterised in the browser with a self-hosted, pinned PDF.js, and the result
 * is fed into `localUrl` — the same slot a JPEG's object URL goes into. That
 * one decision is why the rest of this file did not have to change: the src
 * selection, `localSafe`, `proofSrc` and `makeProof` all work exactly as they
 * do for a raster, and **the Proof is therefore built from pixels that never
 * crossed an origin.** The PDF Proof does not wait on item 112 and never did.
 *
 * The item 113 note above stands and is the reason this is client-side at all:
 * the server cannot rasterise PDF on this stack and that was settled months
 * before it was written down.
 *
 * NOT A CDN. `uploader_complement.js` has been pulling PDF.js from
 * cdnjs.cloudflare.com on every sticker order — a third-party host in the order
 * path, the same defect as DUMMYIMAGE-IN-ORDER-PATH. 8.5 self-hosts the pinned
 * build and that file now points at it, so the theme carries one copy from one
 * origin.
 *
 * AND `gw_savePreview` HAS A CALLER, which an earlier note in this repo denied.
 * It is `uploader_complement.js`, live on the sticker pages the whole time.
 * The sign pages are what had none. They do now.
 *
 * THE BADGE NEVER COMES FROM A PREVIEW. A fallback preview is a picture of the
 * artwork, not the artwork: it is a server-side rendering at the server's
 * chosen size, and measuring it would report that size as the customer's
 * resolution. `dpiFor` reads `headerDims` and nothing else, `readImageDimensions`
 * is PNG/JPEG/WebP only, so an undecodable face keeps no badge and no reading —
 * and `properties[DPI]` keeps the worse-measurable-face rule, which on a
 * two-face line with one unreadable face is the other face's number alone.
 *
 * ── 8.7 ─────────────────────────────────────────────────────────────────────
 * DIAGNOSTIC-EVENT (item 145). One custom event, `sign_artwork_uploaded`,
 * fired ONCE PER PAGE VIEW from reconcile() when the front Design lands.
 *
 *   - ONCE PER PAGE VIEW, not once per file. A replace, a remove-then-
 *     reupload and a retry after a failure all change Design, and all are the
 *     same visitor getting as far as uploading artwork. `uploadEventSent`
 *     answers the question being asked. 8.6's reselect() redraw does not
 *     change Design at all, so it was already safe.
 *   - IT CANNOT BREAK THE GATE. Both calls are typeof-guarded and wrapped in
 *     try/catch: a missing, broken or consent-suppressed tracker must never
 *     throw inside reconcile() and take applyGate() down with it. A
 *     diagnostic is worth nothing next to an order that cannot be placed.
 *   - THE PAYLOAD CARRIES NO ARTWORK AND NO CUSTOMER. Extension only, never
 *     the filename; no /uploads/ URL, no Design/Preview/Proof/DesignState, no
 *     Notes. `preview` uses hasPreview(), the same predicate the redraw path
 *     uses — it reads the form inputs and localUrl, none of which enrich()
 *     builds, so it is already true at this point in the tick.
 *
 * ── 8.6 ─────────────────────────────────────────────────────────────────────
 * CHIP-ROWS-ALWAYS-VISIBLE (item 123). The Orientation and Points rows moved
 * out of [data-preview] in the snippet, so the one element this file hides no
 * longer hides the CHOICE along with the die. Three consequences live here:
 *
 *   - THE CHIPS ARE BUILT AT INIT. renderOrientationChips/renderPointsChips are
 *     the same two functions, called earlier — not a second copy. A page with
 *     no artwork now has rows with chips in them, which is the whole item.
 *   - A CHIP CHANGE PICKS A PATH (`reselect`). With artwork AND a picture to
 *     redraw it is 8.5's behaviour unchanged: invalidate Proof and DesignState
 *     and re-run the whole pass. Without one — nothing uploaded, or an EPS the
 *     browser and the server both refuse to render — there is nothing to draw,
 *     so it takes the state-only path: no enrich(), NO NETWORK, no error line.
 *   - THE ORDER STILL CARRIES THE CHOICE either way. `applySelection` writes
 *     Size/Orientation/Config, re-presses the chips, re-labels the slots and
 *     re-checks the gate, and enrich() opens with exactly that call — one
 *     definition, so the two paths cannot drift.
 *
 * NOTHING HIDES THE ROWS. clearEnrichment and enrich()'s two failure paths go
 * on hiding [data-preview] exactly as they did; the rows are simply no longer
 * in it, which is why the selection now survives a remove and a replace.
 *
 * 8.5 CHANGES NOTHING HERE AND THAT IS THE POINT. A rasterised page is a
 * picture at OUR chosen 1200 px; measuring it would report our scale as the
 * customer's resolution. `readImageDimensions` returns null for a PDF, so the
 * badge is absent and `DPI` comes from whichever face could actually be
 * measured. A PDF does carry a physical page size and an embedded raster does
 * have a real DPI — deliberately not read here; it would be the first badge in
 * this file sourced from anything but the header, and "no reading" is already
 * the honest answer.
 */
(function () {
  'use strict';

  /* PUSH-SUCCESS-NOT-LIVE marker (item 38). Bump when this file changes. */
  var SIGN_UPLOADER_VERSION = '8.8';

  /**
   * MAJOR VERSION ONLY.
   *
   * This was an exact '6.1' match. sign-selector.js:37 already carries this note
   * because the same constant, pinned exactly, silently took the whole selector
   * out on the live page when 8b's bundle went 6.0 -> 6.1; the fix was applied
   * there and never here. A 6.2 bundle would have done it again, to this file:
   * no preview, no DPI badge, no Proof, no DesignState on the two live sign
   * pages, failing exactly the way the rule says it fails — indistinguishably
   * from "no uploader was ever built", with the widget still gating the button
   * so nothing is SOLD wrong and nothing looks wrong either.
   *
   * A minor bump means the surface grew. Pin the major, which is what actually
   * changes when a name this file reads is removed or reshaped.
   */
  var REQUIRED_SIGN_CORE_MAJOR = '6.';

  var ENDPOINT = 'https://api.stickerplease.com/index.php?store=stickerplease';
  var PREVIEW_PX_PER_IN = 40;
  var PROOF_LONGEST_EDGE = 1200;
  var MAX_LOCAL_PROOF_MP = 25;          /* the server's own derive threshold */
  var LOCALLY_RENDERABLE = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

  /* ── 8.5: client-side PDF rasterisation (item 117) ────────────────────────
   * Bounds, and why each one is where it is.
   *
   * PDF_MAX_BYTES 20 MB — the INPUT cap, and it is the binding one. A 1200 px
   * JPEG at q0.85 lands at 100–400 KB, so the server's 8 MB ceiling on a saved
   * preview (helpers.php:415) is never what stops us; an input past this is
   * where parse time stops being predictable.
   *
   * PDF_MAIN_THREAD_MAX_BYTES 2 MB — tier 3 only. Measured on 2026-09-19: with
   * a real Worker the longest main-thread block is 0 ms at 1x CPU and 56 ms at
   * 6x; through the fake worker it is 176 ms at 1x and 1061 ms at 6x on a
   * 24x18in page wrapping a 4032x3024 photo. A second of frozen UI is not a
   * fallback, it is a worse bug than no preview — so tier 3 is for small files
   * or it does not run.
   *
   * PDF_TIMEOUT_MS 10 s — the worst measured case was 1.16 s, so this catches
   * only a pathological file, never a slow one.
   */
  var PDF_MAX_BYTES = 20 * 1024 * 1024;
  var PDF_MAIN_THREAD_MAX_BYTES = 2 * 1024 * 1024;
  var PDF_TIMEOUT_MS = 10000;

  /* 8.8: the product → spec map moved to sign-products.js (window.SignProducts),
     read at init — the selector needs it too, and runs first. See that file. */

  /* ── 8.8: the fold line (fold-outs, Session I-3) ──────────────────────────
   * A NON-PRINTING CREASE GUIDE. Drawn by `draw()`, so it appears on the
   * preview and on the customer Proof JPEG — both are pictures FOR the customer.
   * It can never reach production: on this surface `Design` is the customer's
   * original upload and nothing drawn here is ever written to it.
   *
   * Red dashes over a white underlay, so the line survives on red artwork; a
   * solid tick in the bleed band above and below the trim; and the words.
   * Dashing and the label carry the meaning — colour is the third signal, and
   * it is the one colour the cut (#121212) and safe (#334fb4) guides do not use.
   */
  var FOLD_COLOR = '#d0021b';
  var FOLD_LABEL = 'Fold line — does not print';
  var GUIDE_FONT = 'system-ui, -apple-system, Helvetica, Arial, sans-serif';

  /** Exactly the app's signTierBadge copy ([productId].tsx:247-252). */
  var TIER_LABEL = {
    ok: 'Print ready',
    warn: 'Lower resolution — may look soft up close',
    low: 'Too low for a clear print'
  };
  var TIER_CLASS = { ok: 'is-ok', warn: 'is-warn', low: 'is-low' };

  function extOf(name) {
    var m = /\.([^.]+)$/.exec(String(name || '').toLowerCase());
    return m ? m[1] : '';
  }

  /**
   * Upload one blob and return BOTH urls the server offers.
   *
   * 8.4 widens the resolved value from a bare string to `{ url, previewUrl }`.
   * `gw_uploadFile` has always answered with a `preview_url` beside `file_url`
   * for the formats it can render server-side — TIFF, PSD, and any raster over
   * its own 25 MP derive threshold — and this helper threw it away, so the back
   * face had no picture for exactly the files the front already shows one for.
   * `previewUrl` is null when the server produced none, which is the normal
   * case for a browser-renderable raster and the permanent case for PDF, AI and
   * EPS: the server renders no preview for those, so there is nothing to fall
   * back TO and the slot says so rather than showing a blank die.
   *
   * `url` is still the print file and still the only thing a property carries.
   */
  function post(blob, filename) {
    var fd = new FormData();
    fd.append('action', 'gw_uploadFile');
    fd.append('input_name', 'file');
    fd.append('file', blob, filename);
    return fetch(ENDPOINT, { method: 'POST', body: fd })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (!j || !j.file_url) throw new Error((j && j.message) || 'no file_url in response');
        return { url: j.file_url, previewUrl: j.preview_url || null };
      });
  }

  /**
   * IS THIS A PDF? BY HEADER, NEVER BY EXTENSION.
   *
   * An Illustrator file saved with PDF compatibility — which is the default —
   * IS a PDF, and customers send `.ai` constantly. One saved without it is not,
   * and no amount of believing the extension makes it render. `.eps` is
   * PostScript and is not a PDF at all; it stays "as supplied", deliberately,
   * and this is the line that says so to whoever tries to "fix" it later.
   *
   * Takes the buffer the header probe already read, so this costs no second
   * read of the file.
   */
  function looksLikePdf(buf) {
    var b = new Uint8Array(buf);
    return b.length >= 5 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 &&
           b[3] === 0x46 && b[4] === 0x2d;       /* %PDF- */
  }

  /**
   * Load PDF.js, once, and only when a PDF has actually been chosen.
   *
   * SELF-HOSTED AND PINNED (3.11.174, the legacy build). Not a CDN: a
   * third-party host in the order path is the same defect as
   * DUMMYIMAGE-IN-ORDER-PATH, and `uploader_complement.js` carried exactly that
   * until this step. The legacy build is the one that matches the es2018 floor
   * the rest of the theme is built to, and 3.11.x is the last line shipping a
   * classic script — 4.x and 5.x are ESM only, which would raise that floor for
   * one feature.
   *
   * THE URLS COME FROM LIQUID, because `asset_url` resolves at render and a
   * script injected at file-pick time cannot call it. Two data attributes on
   * the block, ~120 bytes of HTML, and nothing is fetched until a PDF appears.
   */
  var pdfJsPromise = null;
  function loadPdfJs(root) {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    if (pdfJsPromise) return pdfJsPromise;
    var src = root.getAttribute('data-pdfjs');
    if (!src) return Promise.reject(new Error('no data-pdfjs on the block'));
    pdfJsPromise = new Promise(function (res, rej) {
      var el = document.createElement('script');
      el.src = src;
      el.onload = function () {
        if (window.pdfjsLib) res(window.pdfjsLib);
        else rej(new Error('pdf.min.js loaded but pdfjsLib is undefined'));
      };
      el.onerror = function () { rej(new Error('could not load pdf.min.js')); };
      document.head.appendChild(el);
    });
    return pdfJsPromise;
  }

  /**
   * Point PDF.js at its worker. Three tiers, in order, and the order is the
   * whole point.
   *
   *   1. A REAL WORKER from the asset URL. `asset_url` emits the shop's own
   *      domain on this store (//stickerplease.com/cdn/shop/t/N/assets/…), so
   *      the worker script is SAME-ORIGIN and `new Worker(url)` is simply
   *      legal. Measured: the main thread never blocks.
   *   2. A BLOB-URL WORKER. Kept even though tier 1 works today, because the
   *      asset host could become cdn.shopify.com on a Shopify change and this
   *      is what survives that — the host already sends
   *      `Access-Control-Allow-Origin: *`, so fetching the source is allowed.
   *      Costs one extra fetch of ~1.1 MB; measured at parity with tier 1.
   *   3. THE MAIN THREAD, under a size gate. See PDF_MAIN_THREAD_MAX_BYTES.
   *
   * Returns a promise for the mode actually used, so DesignState and the tests
   * can say which one ran rather than guess.
   */
  function configureWorker(lib, workerSrc, fileSize) {
    if (!workerSrc) return Promise.resolve('main');
    try {
      var w = new Worker(workerSrc);        /* same-origin: constructs or throws */
      w.terminate();
      lib.GlobalWorkerOptions.workerSrc = workerSrc;
      return Promise.resolve('worker');
    } catch (e) { /* fall through */ }

    return fetch(workerSrc).then(function (r) {
      if (!r.ok) throw new Error('worker fetch ' + r.status);
      return r.text();
    }).then(function (txt) {
      var url = URL.createObjectURL(new Blob([txt], { type: 'text/javascript' }));
      var w2 = new Worker(url);
      w2.terminate();
      lib.GlobalWorkerOptions.workerSrc = url;
      return 'blob';
    }).catch(function () {
      /* Tier 3, and only for a file small enough that the freeze is not the
         worse outcome. Above the gate we would rather show nothing. */
      if (fileSize > PDF_MAIN_THREAD_MAX_BYTES) {
        throw new Error('no worker available and the file is too large for the main thread');
      }
      lib.GlobalWorkerOptions.workerSrc = '';
      return 'main';
    });
  }

  /**
   * Page 1 of a PDF to a white-matted JPEG blob.
   *
   * PAGE 1 ONLY — a later page is not the artwork. The customer template is two
   * pages and page 1 is the die.
   *
   * WHITE FILL BEFORE THE RENDER, matching gw_generate_preview() and the server
   * path, so a transparent PDF does not composite onto black.
   *
   * JPEG AND NOT PNG — gw_savePreview accepts PNG/JPEG/WebP and re-encodes to
   * JPEG 82 regardless (helpers.php:427-445), so a PNG is bytes uploaded to be
   * thrown away.
   *
   * @returns {Promise<{blob: Blob, mode: string, pages: number}>}
   */
  function renderPdfFirstPage(root, file, longestEdge) {
    if (file.size > PDF_MAX_BYTES) {
      return Promise.reject(new Error('pdf over the ' + PDF_MAX_BYTES + ' byte cap'));
    }
    var timer = null;
    var task = null;
    var work = loadPdfJs(root).then(function (lib) {
      return configureWorker(lib, root.getAttribute('data-pdfjs-worker'), file.size)
        .then(function (mode) { return { lib: lib, mode: mode }; });
    }).then(function (ctx) {
      return file.arrayBuffer().then(function (buf) {
        task = ctx.lib.getDocument({ data: new Uint8Array(buf), isEvalSupported: false });
        return task.promise.then(function (doc) { return { doc: doc, mode: ctx.mode }; });
      });
    }).then(function (ctx) {
      return ctx.doc.getPage(1).then(function (page) {
        var base = page.getViewport({ scale: 1 });
        var scale = longestEdge / Math.max(base.width, base.height);
        if (!isFinite(scale) || scale <= 0) scale = 1;
        var vp = page.getViewport({ scale: scale });
        var cv = document.createElement('canvas');
        cv.width = Math.max(1, Math.round(vp.width));
        cv.height = Math.max(1, Math.round(vp.height));
        var c = cv.getContext('2d');
        c.fillStyle = '#ffffff';
        c.fillRect(0, 0, cv.width, cv.height);
        return page.render({ canvasContext: c, viewport: vp }).promise.then(function () {
          return new Promise(function (res, rej) {
            cv.toBlob(function (b) {
              if (b) res({ blob: b, mode: ctx.mode, pages: ctx.doc.numPages });
              else rej(new Error('toBlob returned null'));
            }, 'image/jpeg', 0.85);
          });
        }).then(function (out) {
          try { ctx.doc.destroy(); } catch (e) {}
          return out;
        });
      });
    });

    var timeout = new Promise(function (_res, rej) {
      timer = setTimeout(function () {
        try { if (task) task.destroy(); } catch (e) {}
        rej(new Error('pdf render timed out after ' + PDF_TIMEOUT_MS + 'ms'));
      }, PDF_TIMEOUT_MS);
    });

    return Promise.race([work, timeout]).then(function (v) {
      clearTimeout(timer); return v;
    }, function (err) {
      clearTimeout(timer); throw err;
    });
  }

  /**
   * The stored basename, recovered from a URL the server built.
   *
   * gw_finalize_upload writes `$uploads_url . rawurlencode($file['name'])` and
   * gw_savePreview validates `basename($_POST['name'])` against what is on
   * disk — so decodeURIComponent of the last path segment is the exact inverse
   * of the encoder, and the only thing the server will accept.
   */
  function basenameFromUrl(u) {
    if (!u) return '';
    var clean = String(u).split('?')[0].split('#')[0];
    var last = clean.split('/').pop() || '';
    try { return decodeURIComponent(last); } catch (e) { return last; }
  }

  /**
   * Attach a rendered preview to an upload that already exists.
   *
   * NOT `post()`. That calls gw_uploadFile and would create a SECOND upload;
   * this is gw_savePreview, which writes `<name>.preview.jpg` beside the
   * original and returns its url. Same endpoint, same store param, different
   * action and a different meaning.
   *
   * Best-effort by construction: the caller never waits on it, because the
   * on-die preview and the Proof both come from the LOCAL blob. A failure here
   * costs the fulfiller's server-side copy and nothing the customer can see.
   */
  function savePreview(name, blob) {
    var fd = new FormData();
    fd.append('action', 'gw_savePreview');
    fd.append('name', name);
    fd.append('preview', blob, 'preview.jpg');
    return fetch(ENDPOINT, { method: 'POST', body: fd })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (!j || j.status !== 'success' || !j.preview_url) {
          throw new Error((j && j.message) || 'gw_savePreview returned no preview_url');
        }
        return j.preview_url;
      });
  }

  function init() {
    var root = document.querySelector('[data-sign-uploader]');
    if (!root) return;

    /* Never form.id — two name="id" inputs make it a RadioNodeList (item 50). */
    var form = root.closest('form[action*="/cart/add"]');
    if (!form) {
      console.warn('[sign-uploader] not inside a /cart/add form — the block must ' +
        'precede buy_buttons in block_order (item 49).');
      return;
    }

    var specMap = window.SignProducts && window.SignProducts.SPEC_BY_PRODUCT_ID;
    var specId = specMap ? specMap[root.getAttribute('data-product-id')] : undefined;
    var coreVersion = window.SignCore && window.SignCore.SIGN_CORE_VERSION;
    if (!specId || typeof coreVersion !== 'string' ||
        coreVersion.indexOf(REQUIRED_SIGN_CORE_MAJOR) !== 0) {
      console.warn('[sign-uploader] SignCore ' + REQUIRED_SIGN_CORE_MAJOR + 'x or spec "' + specId +
        '" unavailable (got ' + (coreVersion || 'nothing') +
        '); the widget still gates add-to-cart, properties stay empty.');
      return;
    }
    var SC = window.SignCore;
    /* NOT const: the Points row swaps this for its registry sibling. `specId`
       follows it, so the two Config writes below stay `set('Config', specId)`. */
    var spec = SC.SIGN_SPECS[specId];
    var size = String(spec.label).split(' ')[0];

    var props = {};
    var nodes = root.querySelectorAll('[data-sign-prop]');
    for (var i = 0; i < nodes.length; i++) props[nodes[i].getAttribute('data-sign-prop')] = nodes[i];
    var set = function (k, v) { if (props[k]) props[k].value = v; };
    var get = function (k) { return props[k] ? props[k].value : ''; };

    var previewBox = root.querySelector('[data-preview]');
    var noPreview = root.querySelector('[data-nopreview]');
    var canvas = root.querySelector('[data-preview-canvas]');
    var badge = root.querySelector('[data-dpi-badge]');
    var orientRow = root.querySelector('[data-orientation-row]');
    var orientChips = root.querySelector('[data-orientation-chips]');
    var pointsRow = root.querySelector('[data-points-row]');
    var pointsChips = root.querySelector('[data-points-chips]');
    var frontLabel = root.querySelector('[data-front-label]');
    var backLabel = root.querySelector('[data-back-label]');
    var backSlot = root.querySelector('[data-back-slot]');
    var backInput = root.querySelector('[data-back-input]');
    var backHint = root.querySelector('[data-back-hint]');
    var backCanvas = root.querySelector('[data-back-canvas]');
    var backBadge = root.querySelector('[data-back-badge]');
    var backStatus = root.querySelector('[data-back-status]');
    var gateNote = root.querySelector('[data-gate-note]');
    var templateLink = root.querySelector('[data-template-link]');
    var foldInfo = root.querySelector('[data-foldout-info]');
    var foldHelp = root.querySelector('[data-foldout-help]');

    /* ── state ─────────────────────────────────────────────────────────── */
    var file = null;          /* the customer's File, captured before upload */
    var headerDims = null;    /* {w,h} from the ORIGINAL's first 64 KB, or null */
    var localUrl = null;      /* object URL, only for browser-renderable files */
    var previewSource = 'none';   /* 'local' | 'client-pdf' | 'backend' | 'none' */
    var pdfToken = 0;             /* a second file chosen mid-render must win */
    var orientation = spec.defaultOrientation;
    var enrichToken = 0;      /* guards against an orientation change mid-upload */
    var uploadEventSent = false;     /* once per page view — a replace is not a second visitor */

    /**
     * Sides is a VARIANT option, not ours: sign-selector.js owns it and resolves
     * its option position by name. This file reads the answer and never the
     * selects — duplicating the position logic here is exactly what that file's
     * `optionPositions` comment exists to prevent. Latched value first (the two
     * deferred scripts init in document order and either may be second), then
     * the event for every change after that.
     */
    var sides = (window.SignSelector && window.SignSelector.selection &&
                 window.SignSelector.selection.sides) || 'single';
    /* ONE FACE, ALWAYS (8.8, fold-outs). The spec says there is no back, so
       `sides` is the constant 'single' whatever arrives — no back slot, no
       DesignBack, no two-face proof can be reached. The selector already sends
       'single' for these specs; this is the second lock, not the first. */
    var oneFace = spec.printedFaces === 1;
    if (oneFace) sides = 'single';

    /* ── the back face (8.3) ───────────────────────────────────────────── */
    var backFile = null;      /* the customer's File for the back */
    var backHeaderDims = null;/* {w,h} from ITS first 64 KB — rule 1 applies per face */
    var backLocalUrl = null;  /* object URL, for its own preview and the proof */
    var backPreviewUrl = null;/* the SERVER's rendering, when the browser has none (8.4) */
    var backPreviewSource = 'none';
    var backPdfBlob = null;   /* 8.5: page 1, held until post() names the file */
    var backUrl = '';         /* the uploaded URL — this IS properties[DesignBack] */
    var backError = null;     /* a visible reason, because this one holds the gate */
    var backPending = false;
    var backToken = 0;        /* a second back file mid-upload must win */
    var gateHeldByUs = false; /* we disabled the button and may undo THAT */

    /* ── recon 3: capture-phase change fires before the widget's own handler,
       so files[0] is available before the upload starts. The widget never
       replaces the input element, only clears its value. ─────────────────── */
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.type !== 'file' || !t.files || !t.files[0]) return;
      /* THE BACK SLOT IS A FILE INPUT INSIDE THIS BLOCK TOO. Without this line
         it would run the front's handler and the front's DPI badge would start
         describing the back file — the badge would be wrong about the file it
         names, silently, which is the one thing rule 1 exists to prevent. */
      if (backInput && t === backInput) return;
      file = t.files[0];
      headerDims = null;
      var token = ++pdfToken;
      if (localUrl) { URL.revokeObjectURL(localUrl); localUrl = null; }

      var ext = extOf(file.name);
      var isLocal = LOCALLY_RENDERABLE.indexOf(ext) >= 0;
      if (isLocal) localUrl = URL.createObjectURL(file);
      previewSource = isLocal ? 'local' : 'none';

      /* Header only. HEADER_PROBE_BYTES is 64 KB; the rest is never read. */
      var f = file;
      f.slice(0, SC.HEADER_PROBE_BYTES).arrayBuffer().then(function (buf) {
        if (token !== pdfToken) return;
        headerDims = SC.readImageDimensions(new Uint8Array(buf));

        /* 8.5, item 117. THE SAME 64 KB ANSWERS BOTH QUESTIONS — the header
           probe is already in hand, so the %PDF- sniff costs no second read.
           readImageDimensions returned null for this file (it is PNG/JPEG/WebP
           only), which is exactly why the badge stays absent below. */
        if (isLocal || !looksLikePdf(buf)) return;

        return renderPdfFirstPage(root, f, PROOF_LONGEST_EDGE).then(function (out) {
          if (token !== pdfToken) return;
          /* INTO localUrl, DELIBERATELY. Everything downstream — the src
             selection in enrich(), localSafe, proofSrc, makeProof — then works
             byte for byte as it does for a JPEG, and the Proof is built from
             pixels that never crossed an origin. That is what makes this
             independent of item 112. */
          localUrl = URL.createObjectURL(out.blob);
          previewSource = 'client-pdf';
          console.log('[sign-uploader] PDF page 1 rasterised (' + out.mode +
            ' worker, ' + out.pages + ' page(s))');

          /* Non-fatal, and nobody waits on it: the preview and the Proof are
             already local. This is only the server's copy. */
          var stored = basenameFromUrl(designUrl());
          if (stored) {
            savePreview(stored, out.blob).catch(function (err) {
              console.warn('[sign-uploader] gw_savePreview failed; the local preview stands', err);
            });
          }

          /* The upload may have landed while we rendered, in which case enrich
             already ran against no preview. Re-run — the same thing the back
             upload does when it completes. */
          if (designUrl()) enrich();
        }).catch(function (err) {
          if (token !== pdfToken) return;
          /* AS SUPPLIED. Every failure lands here — no PDF.js, no worker and
             too big for the main thread, encrypted, corrupt, timed out — and
             every one of them degrades to exactly today's behaviour. The gate
             is the widget's and is never touched. */
          console.warn('[sign-uploader] PDF preview unavailable; the file will print as supplied', err);
        });
      }).catch(function (err) {
        console.warn('[sign-uploader] could not read the file header', err);
      });
    }, true);

    function designUrl() {
      var el = form.querySelector('input[data-spq-prop="Design"]');
      return el && el.value ? el.value : '';
    }
    function previewUrlFromWidget() {
      var el = form.querySelector('input[data-spq-prop="Preview"]');
      return el && el.value ? el.value : '';
    }

    /* ── geometry ──────────────────────────────────────────────────────── */
    function geometry() {
      var r = SC.resolveSign(spec, orientation);
      return {
        r: r,
        bleedW: r.zones.bleed.widthIn, bleedH: r.zones.bleed.heightIn,
        cutW: r.zones.cut.widthIn, cutH: r.zones.cut.heightIn,
        safeW: r.zones.safe.widthIn, safeH: r.zones.safe.heightIn,
        bleedIn: r.zones.bleed.offsetIn, safeIn: -r.zones.safe.offsetIn
      };
    }

    /** contain-fit natural dims inside the cut rect; returns inches. */
    function fitToCut(g, natW, natH) {
      var s = Math.min(g.cutW / natW, g.cutH / natH);
      return { wIn: natW * s, hIn: natH * s };
    }

    /**
     * COVER-fit natural dims over the bleed box; returns inches.
     *
     * The sibling of `fitToCut`, and the difference is min vs max. A contain-fit
     * is right for a rectangle: the whole artwork is visible and the white band
     * outside the cut is the bleed doing its job. On a silhouette it is wrong —
     * contain leaves white wedges inside the die wherever the outline is not a
     * rectangle, which on the arrow is most of the left half. Cover fills the
     * bleed box, the clip takes the overhang, and the die is solid artwork.
     *
     * Cover means part of the artwork IS cropped. That is why the DPI badge is
     * read off this same fit — the covered width, not the contained one, is the
     * size the file is actually printed at, and it is the larger of the two.
     */
    function fitToBleed(g, natW, natH) {
      var s = Math.max(g.bleedW / natW, g.bleedH / natH);
      return { wIn: natW * s, hIn: natH * s };
    }

    /**
     * The one selector, so the badge, the preview, the proof and DesignState
     * can never disagree about where the artwork landed.
     *
     * Keyed on `outlinePaths`, not on `shape` and not on the spec id: a config
     * with no traced die has nothing to clip to, so cover-fit would crop for no
     * reason. Every rectangle keeps contain-fit, byte for byte.
     */
    function fitArtwork(g, natW, natH) {
      return g.r.outlinePaths ? fitToBleed(g, natW, natH) : fitToCut(g, natW, natH);
    }

    /**
     * Put the outline's inch space onto the canvas.
     *
     * The `d` strings are in inches with the CUT path's bbox at the origin, so
     * the translate is the cut rect's own top-left and the scale is `pxPerIn` —
     * the paths are then used verbatim, with no pre-scaled copy to keep in step.
     *
     * The mirror is one matrix (`spec.ts`: no path math enters this codebase).
     * A point at x lands where `2 * axis - x` would unmirrored, so the die
     * reflects about its real centre. `mirrorAxisIn` is the trim bbox centre,
     * 11.9991 and not a rounded 12, which is what puts the mirrored tip at 0.
     */
    function applyOutlineTransform(c, g, pxPerIn, W, H) {
      var cutX = (W - g.cutW * pxPerIn) / 2, cutY = (H - g.cutH * pxPerIn) / 2;
      if (g.r.mirror) {
        c.setTransform(-pxPerIn, 0, 0, pxPerIn, cutX + 2 * g.r.mirrorAxisIn * pxPerIn, cutY);
      } else {
        c.setTransform(pxPerIn, 0, 0, pxPerIn, cutX, cutY);
      }
    }

    /**
     * The crease guides, from `resolveSign`'s `folds` — recomputed on EVERY
     * draw, so the line follows zoom, re-fit, orientation and spec changes with
     * nothing cached. `bleedXIn` is measured from the bleed edge, which is this
     * canvas's own x = 0. Empty on every sign that does not fold, so this is a
     * no-op on the rectangles and the arrow. (`|| []`: a pre-6.3 bundle.)
     *
     * SIZED FOR THE SCREEN IT IS SEEN ON. The preview canvas is drawn at 40 px/in
     * — 1930 px for a 48" sign — and shown in a ~550 px column, so a guide sized
     * in canvas pixels would arrive at a quarter of its weight and the label
     * would be unreadable. `k` is the downscale the browser applies to an
     * on-page canvas; an offscreen one (the Proof, viewed at its own size) has
     * no layout box and keeps k = 1.
     */
    function drawFolds(cv, c, g, pxPerIn, W, H) {
      var folds = g.r.folds || [];
      if (!folds.length) return;
      var k = cv.clientWidth > 0 ? Math.max(1, W / cv.clientWidth) : 1;
      var top = (H - g.cutH * pxPerIn) / 2, bottom = top + g.cutH * pxPerIn;
      var lw = Math.max(1.5, pxPerIn / 16) * k;
      c.save();
      c.setTransform(1, 0, 0, 1, 0, 0);
      folds.forEach(function (f) {
        var x = f.bleedXIn * pxPerIn;

        /* Ticks: solid, in the bleed band outside the trim, top and bottom. */
        c.setLineDash([]);
        c.strokeStyle = '#ffffff'; c.lineWidth = lw * 3;
        c.beginPath(); c.moveTo(x, 0); c.lineTo(x, top); c.moveTo(x, bottom); c.lineTo(x, H); c.stroke();
        c.strokeStyle = FOLD_COLOR; c.lineWidth = lw * 2;
        c.beginPath(); c.moveTo(x, 0); c.lineTo(x, top); c.moveTo(x, bottom); c.lineTo(x, H); c.stroke();

        /* The line across the trim: white underlay, red dashes on it. */
        c.strokeStyle = '#ffffff'; c.lineWidth = lw * 2;
        c.beginPath(); c.moveTo(x, top); c.lineTo(x, bottom); c.stroke();
        c.strokeStyle = FOLD_COLOR; c.lineWidth = lw;
        c.setLineDash([Math.max(pxPerIn * 0.3, 6 * k), Math.max(pxPerIn * 0.18, 4 * k)]);
        c.beginPath(); c.moveTo(x, top); c.lineTo(x, bottom); c.stroke();
        c.setLineDash([]);

        /* The label, beside the top tick, on a white tab so it reads on any art. */
        var fs = Math.round(Math.max(11, pxPerIn * 0.32) * k);
        var pad = Math.round(fs * 0.3);
        c.font = '600 ' + fs + 'px ' + GUIDE_FONT;
        c.textBaseline = 'top';
        var tw = c.measureText(FOLD_LABEL).width;
        var lx = x + lw * 2 + pad;
        if (lx + tw + pad > W) lx = x - lw * 2 - pad - tw;   /* no room right: go left */
        var ly = top + pad;
        c.fillStyle = 'rgba(255,255,255,0.9)';
        c.fillRect(lx - pad, ly - pad / 2, tw + pad * 2, fs + pad);
        c.fillStyle = FOLD_COLOR;
        c.textAlign = 'left';
        c.fillText(FOLD_LABEL, lx, ly);
      });
      c.restore();
    }

    /**
     * Draw sign + artwork + guides at `pxPerIn`. Returns the fitted width in
     * inches, which is the binding axis the DPI reading uses.
     */
    function draw(cv, img, g, pxPerIn) {
      var W = Math.round(g.bleedW * pxPerIn), H = Math.round(g.bleedH * pxPerIn);
      cv.width = W; cv.height = H;
      var c = cv.getContext('2d');
      c.fillStyle = '#ffffff'; c.fillRect(0, 0, W, H);

      var fit = fitArtwork(g, img.naturalWidth || img.width, img.naturalHeight || img.height);
      var dw = fit.wIn * pxPerIn, dh = fit.hIn * pxPerIn;

      if (g.r.outlinePaths) {
        var op = g.r.outlinePaths;

        /* THE CLIP IS MIRRORED, THE ARTWORK IS NOT. `clip()` stores the region
           in device space, so the transform is reset before drawImage and the
           customer's design lands on the mirrored board unchanged — mirroring
           it too would print their text backwards. */
        c.save();
        applyOutlineTransform(c, g, pxPerIn, W, H);
        c.clip(new Path2D(op.bleed));
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
        c.restore();

        /* GUIDES AFTER THE RESTORE, never inside the clip. A stroke straddles
           its path, so under a live clip the cut line loses its outer half and
           comes back at half weight — which reads as a rendering bug rather
           than as the clip it actually is.

           lineWidth and the dash are in USER space under a scaled transform, so
           both are divided by `pxPerIn`: otherwise the guides come out 40x heavy
           in the preview and ~49x heavy in the proof. */
        applyOutlineTransform(c, g, pxPerIn, W, H);
        c.lineWidth = Math.max(1, pxPerIn / 20) / pxPerIn;
        c.strokeStyle = '#121212'; c.setLineDash([]);
        c.stroke(new Path2D(op.trim));            /* outlines' `trim` IS the cut */
        c.strokeStyle = '#334fb4';
        c.setLineDash([1 / 6, 1 / 6]);
        c.stroke(new Path2D(op.safe));
        c.setLineDash([]);
        c.setTransform(1, 0, 0, 1, 0, 0);
        drawFolds(cv, c, g, pxPerIn, W, H);

        return fit.wIn;
      }

      c.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);

      /* guides on top of the artwork */
      var cutX = (W - g.cutW * pxPerIn) / 2, cutY = (H - g.cutH * pxPerIn) / 2;
      c.lineWidth = Math.max(1, pxPerIn / 20);
      c.strokeStyle = '#121212'; c.setLineDash([]);
      c.strokeRect(cutX, cutY, g.cutW * pxPerIn, g.cutH * pxPerIn);

      var safeX = (W - g.safeW * pxPerIn) / 2, safeY = (H - g.safeH * pxPerIn) / 2;
      c.strokeStyle = '#334fb4'; c.setLineDash([pxPerIn / 6, pxPerIn / 6]);
      c.strokeRect(safeX, safeY, g.safeW * pxPerIn, g.safeH * pxPerIn);
      c.setLineDash([]);
      drawFolds(cv, c, g, pxPerIn, W, H);

      return fit.wIn;
    }

    function loadImage(src) {
      return new Promise(function (res, rej) {
        var img = new Image();
        img.onload = function () { res(img); };
        img.onerror = function () { rej(new Error('image failed to load: ' + src)); };
        img.src = src;
      });
    }

    /* ── DesignState v1 (item 56) ──────────────────────────────────────── */
    /**
     * Does this configuration have two DIFFERENT faces?
     *
     * Double-sided AND outlined. Double-sided alone is not enough: a rectangle's
     * back is the same board with the same art, so it keeps one upload and its
     * proof keeps two identical faces. Outlined alone is not enough either — a
     * single-sided arrow is one face. Keyed on `outlinePaths`, never on shape or
     * on the spec id, for the same reason `fitArtwork` is.
     */
    function twoFace(g) {
      return sides === 'double' && !!(g || geometry()).r.outlinePaths;
    }

    /** Rule 1, per face: the ORIGINAL's header against the size it is placed at. */
    function dpiFor(dims, g) {
      if (!dims) return null;
      return SC.effectiveDpi(dims.w, fitArtwork(g, dims.w, dims.h).wIn);
    }

    /**
     * THE LINE'S DPI — the WORSE face when there are two.
     *
     * `properties[DPI]` is one number for one line, and a line is only as good
     * as the face that prints worst: a 300 DPI front beside a 24 DPI back is not
     * a 300 DPI order, and saying so on the order would put the conversation
     * about a soft print in exactly the wrong place. The per-face badges keep
     * their OWN readings — that is what a per-face badge means, and it is what
     * tells the customer WHICH file to replace — and both survive in
     * DesignState.faces[].dpi, so nothing is lost by the key being pessimistic.
     *
     * An UNMEASURABLE face is not a worse face. `readImageDimensions` is PNG /
     * JPEG / WebP only and `dpiTier(null)` is null by design — "vector art has no
     * DPI and must not read red" (dpi.ts). So this takes the minimum over the
     * faces it could actually measure, and returns null only when it measured
     * none. A face we cannot read is a face we say nothing about, on the line
     * exactly as on its badge.
     */
    function lineDpi(g) {
      var vals = [dpiFor(headerDims, g)];
      if (twoFace(g)) vals.push(dpiFor(backHeaderDims, g));
      var worst = null;
      for (var i = 0; i < vals.length; i++) {
        if (vals[i] === null) continue;
        if (worst === null || vals[i] < worst) worst = vals[i];
      }
      return worst;
    }

    /** Paint one DPI badge. Returns the `N · label` string, or '' when unreadable. */
    function paintBadge(el, dims, g) {
      var dpi = dpiFor(dims, g);
      var tier = SC.dpiTier(dpi);
      if (dpi === null || !tier) { if (el) el.hidden = true; return ''; }
      if (el) {
        el.textContent = Math.round(dpi) + ' DPI — ' + TIER_LABEL[tier];
        el.className = 'sign-uploader__dpi ' + TIER_CLASS[tier];
        el.hidden = false;
      }
      return Math.round(dpi) + ' · ' + TIER_LABEL[tier];
    }

    /**
     * THE GATE, AND THE ONLY PLACE IT IS TOUCHED.
     *
     * Three rules, and each one is a way this could trap a customer:
     *   - We only ever RE-DISABLE a button the widget has already enabled.
     *     `gateHeldByUs` records that we were the one who did it.
     *   - We release only OUR hold, never the widget's.
     *   - We release only while `Design` is still present. If it vanished, the
     *     widget closed its own gate and enabling here would open it on a line
     *     with no artwork at all — the exact failure the whole file is built to
     *     avoid.
     * Called from reconcile (so every widget mutation re-evaluates it), from the
     * Sides listener, and from both ends of the back upload.
     */
    function applyGate() {
      var btn = form.querySelector('button[name="add"]');
      if (!btn) return;
      var hold = !!designUrl() && twoFace() && !backUrl;
      if (hold) {
        if (!btn.disabled) { btn.disabled = true; gateHeldByUs = true; }
        if (gateNote) {
          gateNote.textContent = backError ||
            (backPending ? 'Uploading the back design…' : 'Add the back design to continue.');
          gateNote.hidden = false;
        }
        return;
      }
      if (gateHeldByUs) {
        gateHeldByUs = false;
        if (designUrl()) btn.disabled = false;
      }
      if (gateNote) gateNote.hidden = true;
    }

    function renderBackStatus() {
      if (!backStatus) return;
      var msg = backError ? backError
        : backPending ? 'Uploading…'
        : backUrl ? (backFile ? backFile.name : 'Back design uploaded')
        : '';
      backStatus.textContent = msg;
      backStatus.className = 'sign-uploader__back-status' + (backError ? ' is-error' : '');
      backStatus.hidden = !msg;
    }

    /** Drop the back face entirely. Safe to call when there is none. */
    function clearBack() {
      backToken++;
      backFile = null; backHeaderDims = null; backUrl = ''; backError = null; backPending = false;
      backPreviewUrl = null; backPreviewSource = 'none'; backPdfBlob = null;
      if (backLocalUrl) { URL.revokeObjectURL(backLocalUrl); backLocalUrl = null; }
      if (backInput) backInput.value = '';
      set('DesignBack', '');
      if (backBadge) backBadge.hidden = true;
      renderBackStatus();
    }

    /**
     * The back file: header first (rule 1), then the upload.
     *
     * One chain rather than two, so `backHeaderDims` is settled before the URL
     * lands and the badge can never describe a file the enrich pass has not
     * measured. `post()` is the same helper proof.jpg and design-state.json use
     * — this never goes near upload-widget.js, which owns the front and the gate.
     */
    function onBackFile(f) {
      var token = ++backToken;
      if (backLocalUrl) { URL.revokeObjectURL(backLocalUrl); backLocalUrl = null; }
      backFile = f; backHeaderDims = null; backUrl = ''; backError = null; backPending = true;
      backPreviewUrl = null;
      set('DesignBack', '');
      var backIsLocal = LOCALLY_RENDERABLE.indexOf(extOf(f.name)) >= 0;
      if (backIsLocal) backLocalUrl = URL.createObjectURL(f);
      backPreviewSource = backIsLocal ? 'local' : 'none';
      renderBackStatus(); applyGate(); renderBackPreview();

      f.slice(0, SC.HEADER_PROBE_BYTES).arrayBuffer().then(function (buf) {
        if (token !== backToken) return;
        backHeaderDims = SC.readImageDimensions(new Uint8Array(buf));

        /* 8.5 — the back face gets the same treatment as the front, from the
           same probe buffer. A double-sided arrow whose two faces are both PDFs
           is a Proof with two rasterised faces and no CORS anywhere in it. */
        if (backIsLocal || !looksLikePdf(buf)) return;
        return renderPdfFirstPage(root, f, PROOF_LONGEST_EDGE).then(function (out) {
          if (token !== backToken) return;
          backLocalUrl = URL.createObjectURL(out.blob);
          backPreviewSource = 'client-pdf';
          backPdfBlob = out.blob;
          renderBackPreview();
        }).catch(function (err) {
          if (token !== backToken) return;
          console.warn('[sign-uploader] back PDF preview unavailable; as supplied', err);
        });
      }).catch(function (err) {
        console.warn('[sign-uploader] could not read the back file header', err);
      }).then(function () {
        if (token === backToken) paintBadge(backBadge, backHeaderDims, geometry());
        return post(f, f.name);
      }).then(function (r) {
        if (token !== backToken) return;
        backUrl = r.url; backPending = false; backError = null;
        /* WEB-PREVIEW-BACKEND-FALLBACK (item 98), the back half. The server's
           rendering is the only picture that exists for a TIFF or a PSD, and it
           arrives in the same response as the print url — so the slot that just
           took the file can show it immediately, with no second request and no
           guess about what the browser can decode. */
        backPreviewUrl = r.previewUrl;
        set('DesignBack', r.url);
        /* 8.5: the stored name only exists once the upload has landed, so the
           rendered page waits here rather than racing it. Non-fatal as ever. */
        if (backPdfBlob) {
          var storedBack = basenameFromUrl(r.url);
          if (storedBack) {
            savePreview(storedBack, backPdfBlob).catch(function (err) {
              console.warn('[sign-uploader] back gw_savePreview failed; the local preview stands', err);
            });
          }
        }
        renderBackStatus(); applyGate();
        /* Render here as well as in enrich: a customer may choose the back file
           FIRST, and a back preview that waited for the front would leave the
           slot blank for as long as it took them to pick one. */
        renderBackPreview();
        /* The old Proof and DesignState describe a line with no back. */
        set('Proof', ''); set('DesignState', '');
        if (designUrl()) enrich();
      }).catch(function (err) {
        if (token !== backToken) return;
        backPending = false;
        /* THE EXCEPTION TO RULE 2, stated in the header: this failure is
           visible and keeps the button shut, because half a double-sided
           order is not an order. */
        backError = 'The back design did not upload. Choose it again.';
        console.warn('[sign-uploader] back upload failed; the gate stays shut', err);
        renderBackStatus(); applyGate();
      });
    }

    if (backInput) {
      backInput.addEventListener('change', function () {
        if (backInput.files && backInput.files[0]) onBackFile(backInput.files[0]);
      });
    }

    /**
     * The back face on its own die.
     *
     * The SAME draw() as the front, handed the mirrored geometry the proof sheet
     * already uses. THE ARTWORK IS NOT MIRRORED — draw() resets the transform
     * before drawImage — so the back shows the customer's design the right way
     * round on a die that faces the other way, which is what the press does.
     *
     * Independent of the front: a customer may choose the back file first, and a
     * back preview that waited for the front would look broken until they did.
     */
    function renderBackPreview(g) {
      g = g || geometry();
      /* THE LOCAL FILE FIRST, THE SERVER'S RENDERING SECOND. Local costs no
         request and is the customer's actual bytes; the server's is a rendering
         of them, and the only picture that exists when the browser cannot
         decode the type. Both are display only — see the CORS note in the
         header for why neither the remote one nor a proof built from it can
         reach a canvas's pixels. */
      var src = backLocalUrl || backPreviewUrl;
      if (!backCanvas || !twoFace(g) || !src) return;
      var token = enrichToken;
      loadImage(src).then(function (bimg) {
        if (token === enrichToken) draw(backCanvas, bimg, mirroredGeometry(g), PREVIEW_PX_PER_IN);
      }).catch(function (err) {
        console.warn('[sign-uploader] back preview failed', err);
      });
    }

    /**
     * FACE LABELS BY DIRECTION (item 96, the web half).
     *
     * The app's string, verbatim — `[productId].tsx:706-709`. The founder's own
     * words while ordering #1024: customers name their files by direction and
     * production reads front/back, so the label carries both, and the two
     * surfaces say it identically because a customer who starts on the phone and
     * finishes on the web must not meet two vocabularies for one sign.
     *
     * THE BACK POINTS THE OTHER WAY. That is the whole reason this slot exists:
     * the back die is the front mirrored, so a left-pointing arrow has a
     * right-pointing back. Naming the slot after the die the file lands on is
     * what stops the two files being swapped.
     *
     * A SPEC WITH NO DIRECTION KEEPS THE BARE WORD. `points` is null for every
     * rectangle, and "this sign does not point" is a different fact from "this
     * sign points right" — the same distinction DesignState already draws. The
     * labels are hidden on a one-face sign anyway; this keeps them honest for
     * the day a two-faced shape has no direction.
     */
    function faceLabel(face) {
      var p = spec.points;
      if (!p) return face === 'front' ? 'Front' : 'Back';
      return face === 'front'
        ? 'Front — points ' + p
        : 'Back — points ' + (p === 'right' ? 'left' : 'right');
    }

    /**
     * ONE COPY OF THE WHITELIST, AND IT IS THE SERVER'S (D4).
     *
     * The widget sets `accept` on its own input and writes the accepted types
     * into `.spq-hint`, both from `gw_uploadConfig`. This mirrors those two
     * values onto the back picker rather than restating them.
     *
     * It is not tidiness. The list this slot used to carry in Liquid was eight
     * literal extensions against the server's fifteen, so the back picker had
     * been hiding psd, psb, cdr, gif, tif and tiff from the file dialog on a
     * slot whose upload path accepts all of them — the front's own whitelist,
     * one element away, disagreeing in silence. A mirror cannot drift.
     *
     * Called from `reconcile`, which the MutationObserver already runs on every
     * widget mutation — and the hint arriving IS one, since the widget replaces
     * that paragraph's text node when the config lands.
     */
    function syncBackPicker() {
      if (!backInput) return;
      var wInput = form.querySelector('.spq-input');
      var wHint = form.querySelector('.spq-hint');
      var accept = wInput && wInput.getAttribute('accept');
      if (accept && backInput.getAttribute('accept') !== accept) {
        backInput.setAttribute('accept', accept);
      }
      /* The widget's placeholder while gw_uploadConfig is in flight is not a
         list, and copying it would put "Loading accepted file types…" under a
         button that already works. */
      var hint = wHint && wHint.textContent;
      if (backHint && hint && hint.indexOf('Loading') !== 0 && backHint.textContent !== hint) {
        backHint.textContent = hint;
      }
    }

    /** The slot itself, and the Front label that only makes sense beside a Back. */
    function renderFaceSlots(g) {
      var two = twoFace(g);
      if (backSlot) backSlot.hidden = !two;
      if (frontLabel) {
        frontLabel.hidden = !two;
        frontLabel.textContent = faceLabel('front');
      }
      /* Re-labelled here rather than in the Points handler: every path that can
         change the direction — the chips, a Sides change, the first enrich —
         already ends in this function, and a second write site is a second
         place for the two labels to disagree about one die. */
      if (backLabel) backLabel.textContent = faceLabel('back');
      if (!two && backBadge) backBadge.hidden = true;
    }

    /** Outlined specs only — the template is a die, and a rectangle has none. */
    function renderTemplateLink(g) {
      if (templateLink) templateLink.hidden = !(g || geometry()).r.outlinePaths;
    }

    /**
     * Fold-out copy (8.8). Keyed on `shape === 'fold-out'`, filled here rather
     * than written in Liquid — Liquid cannot see the spec — so every other sign
     * page keeps two EMPTY hidden containers and its visible text is unchanged.
     *
     * WIDTH × HEIGHT, always. Folded size is the widest panel × the height,
     * derived from the spec's folds, never typed.
     */
    function renderFoldoutCopy(g) {
      g = g || geometry();
      var isFold = spec.shape === 'fold-out';
      if (foldInfo) foldInfo.hidden = !isFold;
      if (foldHelp) foldHelp.hidden = !isFold;
      canvas.setAttribute('aria-label', isFold
        ? 'Your artwork placed on the sign, with the cut line, safe margin and fold line'
        : 'Your artwork placed on the sign, with the cut line and safe margin');
      if (!isFold) return;

      var w = g.r.trimIn.w, h = g.r.trimIn.h;
      var edges = [0].concat((g.r.folds || []).map(function (f) { return f.trimXIn; }), [w]);
      var panel = 0;
      for (var i = 1; i < edges.length; i++) panel = Math.max(panel, edges[i] - edges[i - 1]);

      var para = function (cls, text) {
        var p = document.createElement('p');
        p.className = cls;
        p.textContent = text;
        return p;
      };
      if (foldInfo) {
        foldInfo.textContent = '';
        foldInfo.appendChild(para('sign-uploader__foldout-lead', 'Ships folded. Opens wide.'));
        foldInfo.appendChild(para('sign-uploader__foldout-dims',
          'Open: ' + w + ' × ' + h + ' in · Folded: ' + panel + ' × ' + h + ' in'));
      }
      if (foldHelp) {
        foldHelp.textContent = '';
        foldHelp.appendChild(para('sign-uploader__note',
          'Design across the full sign, or make each panel readable on its own. ' +
          'The center crease remains visible on the finished sign.'));
        foldHelp.appendChild(para('sign-uploader__note',
          'Keep small text and logo detail clear of the crease.'));
      }
    }

    function designStateDoc(g, baseRectPx, proofSource) {
      return {
        version: 1,
        product: 'Yard Signs ' + size,
        artboard: { widthIn: g.cutW, heightIn: g.cutH, bleedIn: g.bleedIn, safeIn: g.safeIn },
        canvas: {
          bleedRectPx: { x: 0, y: 0, w: Math.round(g.bleedW * PREVIEW_PX_PER_IN),
                         h: Math.round(g.bleedH * PREVIEW_PX_PER_IN), r: 0 },
          proofSource: proofSource
        },
        artwork: {
          sourceUrl: null,
          isSvg: extOf(file && file.name) === 'svg',
          /* 8.5, ADDITIVE, version stays 1 — the `intake` and `faces`
             precedent. 'client-pdf' says the picture on the die was page 1
             rasterised in this browser, which is a fact the regeneration record
             cannot recover from anything else it stores: the Design is a PDF
             and the baseRect alone does not say what drew it. */
          previewSource: previewSource,
          pixelW: headerDims ? headerDims.w : 0,
          pixelH: headerDims ? headerDims.h : 0,
          baseRect: baseRectPx,
          transform: { x: 0, y: 0, scale: 1, rotation: 0 }
        },
        texts: [],
        /**
         * ADDITIVE AND APPENDED LAST — version stays 1, the `intake` precedent.
         * A v1 reader that has never heard of these ignores them and still reads
         * every field it knows; bumping to v2 would make every existing reader
         * reject the document to learn nothing it needs.
         *
         * `points` is null for a config with no direction (every rectangle), not
         * 'right' — "this sign does not point" and "this sign points right" are
         * different facts and production should not have to guess which one an
         * 18x24 is claiming.
         *
         * `faces` REPLACES 8.2's count. A number said how many faces the Proof
         * showed; an array says what is ON them — which die, mirrored or not,
         * which file, at what size, at what DPI. That is the regeneration record
         * doing its job, and it is where both faces' originals live: `Source`
         * stays ONE key meaning one thing (§3 of the 12b recon), so a second
         * top-level original is not invented here.
         *
         * OMITTED on a single-sided line, so its payload stays byte-comparable
         * to the order-#1022 baseline — the same reason `intake` is omitted on a
         * normal instant flow.
         */
        points: spec.points || null,
        /* `undefined` here means JSON.stringify drops the key outright — which
           is exactly "omitted", not "present and empty". */
        faces: facesDoc(g, baseRectPx)
      };
    }

    /**
     * Both faces, or null when there is only one.
     *
     * A double-sided RECTANGLE gets two entries that are deliberately identical
     * but for `face` — same art, same board, printed twice. That is the truth
     * about a rectangle and it is what its Proof already shows.
     */
    function facesDoc(g, baseRectPx) {
      if (sides !== 'double') return undefined;
      var front = {
        face: 'front',
        specId: specId,
        mirror: !!g.r.mirror,
        /* The web composites nothing, so the Design IS the original and there is
           no second file for `sourceUrl` to name — as at `artwork.sourceUrl`. */
        designUrl: designUrl() || null,
        sourceUrl: null,
        pixelW: headerDims ? headerDims.w : 0,
        pixelH: headerDims ? headerDims.h : 0,
        baseRect: baseRectPx,
        dpi: dpiFor(headerDims, g),
        previewSource: previewSource
      };
      if (!twoFace(g)) {
        var same = {};
        for (var k in front) if (Object.prototype.hasOwnProperty.call(front, k)) same[k] = front[k];
        same.face = 'back';
        return [front, same];
      }
      return [front, {
        face: 'back',
        specId: specId,
        mirror: !g.r.mirror,
        designUrl: backUrl || null,
        sourceUrl: null,
        pixelW: backHeaderDims ? backHeaderDims.w : 0,
        pixelH: backHeaderDims ? backHeaderDims.h : 0,
        baseRect: backHeaderDims ? baseRectFor(g, backHeaderDims) : null,
        dpi: dpiFor(backHeaderDims, g),
        previewSource: backPreviewSource
      }];
    }

    /** The placed rect in preview px, from natural dims. One definition, two faces. */
    function baseRectFor(g, dims) {
      var fit = fitArtwork(g, dims.w, dims.h);
      return {
        x: Math.round((g.bleedW - fit.wIn) / 2 * PREVIEW_PX_PER_IN),
        y: Math.round((g.bleedH - fit.hIn) / 2 * PREVIEW_PX_PER_IN),
        w: Math.round(fit.wIn * PREVIEW_PX_PER_IN),
        h: Math.round(fit.hIn * PREVIEW_PX_PER_IN)
      };
    }

    function clearEnrichment() {
      set('DPI', ''); set('Proof', ''); set('DesignState', '');
      previewBox.hidden = true; noPreview.hidden = true; badge.hidden = true;
      /* The widget's Design went away, so the whole line resets — a back with no
         front is the half-order this file exists to refuse. */
      clearBack();
      renderFaceSlots();
    }

    /**
     * SOURCE SELECTION — the companion wins, and that is a D5 rule, not a
     * preference. The server already downscaled a large original precisely so
     * nothing has to decode it; loading the local file first would decode a
     * 107 MP JPEG in the customer's tab to draw a 970 px preview.
     * The local file is used only when there is no companion AND the header
     * says it is safe (at or under the server's own 25 MP threshold), or when
     * the header is unreadable but the browser can render the type.
     *
     * 8.6 LIFTS THIS OUT OF enrich() SO IT CAN BE ASKED AS A QUESTION. A chip
     * change has to know whether there is a picture to redraw before it decides
     * to redraw one, and the only honest answer is the one enrich() itself
     * would reach — a second, approximate test here ("is there a localUrl?")
     * would send the 25 MP case down the wrong path.
     */
    function previewSources() {
      var companion = previewUrlFromWidget();
      var mp = headerDims ? SC.megapixels(headerDims) : null;
      var localSafe = !!localUrl && (mp === null || mp <= MAX_LOCAL_PROOF_MP);
      return {
        companion: companion,
        localSafe: localSafe,
        src: companion || (localSafe ? localUrl : '')
      };
    }

    /** Artwork that has a picture to redraw — both halves, or neither. */
    function hasPreview() {
      return !!designUrl() && !!previewSources().src;
    }

    /**
     * Everything a die change settles that needs no pixels.
     *
     * The three vocabulary keys, the chips' own pressed state, the slot labels
     * (item 96) and the template link, and the gate. enrich() opens with this
     * exact call, so there is ONE definition of "the selection has been
     * applied" and the no-preview path cannot quietly drift from the preview
     * one.
     *
     * NOT CALLED AT INIT. The three keys stay empty until something lands —
     * the snippet's own promise, and what makes a page with no upload post id
     * and quantity and nothing else. The chips are rendered at init; the
     * PROPERTIES are written the first time the customer answers.
     */
    function applySelection(g) {
      g = g || geometry();
      set('Size', size); set('Orientation', orientation); set('Config', specId);
      renderOrientationChips();
      renderPointsChips();
      renderFaceSlots(g);
      renderTemplateLink(g);
      renderFoldoutCopy(g);
      applyGate();
    }

    /**
     * A chip changed the die. ONE PATH PER STATE, and the state is the question
     * "is there a picture to redraw".
     *
     * WITH ARTWORK AND A PREVIEW this is 8.5 byte for byte: both records
     * describe the old placement, so both are invalidated and the whole pass
     * runs again to rebuild them.
     *
     * WITHOUT ONE — an empty page, or a file neither the browser nor the server
     * will render — enrich() has nothing to draw, and calling it anyway is
     * precisely the defect item 123 is about: a network round trip and a
     * failure line on behalf of a customer who has uploaded nothing. The
     * selection still reaches the order, because the selection IS the order.
     *
     * PROOF AND DesignState ARE CLEARED ON BOTH PATHS AND RE-UPLOADED ON
     * NEITHER. On the empty path they are already empty and this is a no-op,
     * which is what keeps state A to zero requests. On the no-preview path they
     * describe a die the customer has just stopped choosing: an empty
     * regeneration record reads as "none", a stale one reads as a lie about
     * which way the arrow pointed.
     */
    function reselect() {
      set('Proof', ''); set('DesignState', '');
      if (hasPreview()) { enrich(); return; }
      /* ORPHAN ANY PASS STILL IN FLIGHT. The no-preview path still ran a real
         enrich() when the file landed, and its DesignState upload may not have
         resolved yet — it is holding the previous token, and without this bump
         it would write the OLD die's document back over the clear above.
         reconcile() bumps for exactly this reason when the Design goes away. */
      enrichToken++;
      applySelection();
    }

    /* ── the enrichment pass ───────────────────────────────────────────── */
    function enrich() {
      var token = ++enrichToken;
      var g = geometry();

      applySelection(g);

      /* DPI — from the ORIGINAL's header, never from the drawn image. One badge
         per face, each stating ITS OWN face; the property states the LINE, which
         is the worse of the two. On a one-faced sign the badge IS the line and
         the two agree, byte for byte with 8.2. */
      paintBadge(badge, headerDims, g);
      if (twoFace(g)) paintBadge(backBadge, backHeaderDims, g);
      else if (backBadge) backBadge.hidden = true;

      var lineD = lineDpi(g);
      var lineT = SC.dpiTier(lineD);
      set('DPI', lineD !== null && lineT ? Math.round(lineD) + ' · ' + TIER_LABEL[lineT] : '');

      /* The selection itself is `previewSources` above — one definition, so the
         chip handler's guard and this pass can never disagree about whether
         there is a picture. */
      var sel = previewSources();
      var src = sel.src;
      /* 8.5 bookkeeping only. `previewSource` already says 'local' or
         'client-pdf' when we have our own pixels; this records the remaining
         case, where the picture on the die is the SERVER's rendering and the
         Proof therefore cannot be built from it. */
      if (sel.companion && previewSource === 'none') previewSource = 'backend';

      /* A proof can only come from an UNTAINTED canvas, and /uploads/ sends no
         CORS header — so the proof is always the local file or nothing. When the
         preview came from the companion the local image is loaded a second time,
         which is cheap because localSafe capped it at 25 MP. */
      var proofSrc = sel.localSafe ? localUrl : null;

      if (!src) {
        previewBox.hidden = true; noPreview.hidden = false;
        uploadState(g, null, 'none', token);
        return;
      }

      loadImage(src).then(function (img) {
        if (token !== enrichToken) return;
        previewBox.hidden = false; noPreview.hidden = true;
        draw(canvas, img, g, PREVIEW_PX_PER_IN);

        var fit = fitArtwork(g, img.naturalWidth, img.naturalHeight);
        var baseRectPx = {
          x: Math.round((g.bleedW - fit.wIn) / 2 * PREVIEW_PX_PER_IN),
          y: Math.round((g.bleedH - fit.hIn) / 2 * PREVIEW_PX_PER_IN),
          w: Math.round(fit.wIn * PREVIEW_PX_PER_IN),
          h: Math.round(fit.hIn * PREVIEW_PX_PER_IN)
        };

        renderBackPreview(g);
        uploadState(g, baseRectPx, proofSrc ? 'local' : 'none', token);
        if (!proofSrc) return;

        /* The proof needs BOTH faces' pixels when there are two. A back that the
           browser cannot render means no proof at all rather than a sheet whose
           back face quietly shows the front's artwork — `Proof` is evidence, and
           evidence of the wrong thing is worse than none. */
        var frontP = proofSrc === src ? Promise.resolve(img) : loadImage(proofSrc);
        var backP = twoFace(g) && backLocalUrl ? loadImage(backLocalUrl) : Promise.resolve(null);
        Promise.all([frontP, backP]).then(function (pair) {
          if (token !== enrichToken) return;
          if (twoFace(g) && !pair[1]) return;
          makeProof(pair[0], pair[1], g, token);
        }).catch(function (err) {
          console.warn('[sign-uploader] proof source failed', err);
        });
      }).catch(function (err) {
        console.warn('[sign-uploader] preview failed', err);
        previewBox.hidden = true; noPreview.hidden = false;
        uploadState(g, null, 'none', token);
      });
    }

    /* Non-fatal. Never touches the gate. */
    function uploadState(g, baseRectPx, proofSource, token) {
      var doc = designStateDoc(g, baseRectPx, proofSource);
      var blob = new Blob([JSON.stringify(doc)], { type: 'application/json' });
      post(blob, 'design-state.json').then(function (r) {
        if (token === enrichToken) set('DesignState', r.url);
      }).catch(function (err) {
        console.warn('[sign-uploader] DesignState upload failed; falling back to inline', err);
        if (token === enrichToken) set('DesignState', JSON.stringify(doc));
      });
    }

    /**
     * The same geometry with the die reflected, for the back face.
     *
     * A shallow copy, because `draw` reads `g.r.mirror` and nothing else about
     * direction — and because mutating `g.r` would hand the caller back a
     * geometry that quietly disagrees with the spec it came from. For a config
     * with no outline this is the identity: a double-sided rectangle's two
     * faces are the same face, which is the truth about a rectangle.
     */
    function mirroredGeometry(g) {
      if (!g.r.outlinePaths) return g;
      var r2 = {}, g2 = {}, k;
      for (k in g.r) if (Object.prototype.hasOwnProperty.call(g.r, k)) r2[k] = g.r[k];
      r2.mirror = !g.r.mirror;
      for (k in g) if (Object.prototype.hasOwnProperty.call(g, k)) g2[k] = g[k];
      g2.r = r2;
      return g2;
    }

    /**
     * Both faces on one sheet, side by side, labelled.
     *
     * ONE `Proof` KEY, NOT TWO. PROPERTY-VOCABULARY is nine keys and a tenth for
     * a back proof is the same thing direction is already refused. It is also
     * the better proof: double-sided registration is precisely where front/back
     * errors happen, so adjacency IS the check — two URLs is two clicks and a
     * real chance only one gets opened.
     *
     * EACH FACE KEEPS TODAY'S 1200 px, so the sheet is ~2x that wide. Sizing the
     * SHEET to 1200 would put each face at ~600 px, ~24 px/in, too coarse to
     * judge the thing the proof exists to show.
     *
     * The back is the mirrored die carrying the SAME, UNMIRRORED artwork — which
     * is what the press does, and what a customer reading their own text on the
     * back needs to see.
     */
    function drawProofSheet(cv, frontImg, backImg, g, pxPerIn) {
      var faceW = Math.round(g.bleedW * pxPerIn), faceH = Math.round(g.bleedH * pxPerIn);
      var gutter = Math.max(2, Math.round(faceW / 300));
      var band = Math.max(18, Math.round(faceH * 0.08));
      var W = faceW * 2 + gutter, H = band + faceH;

      var front = document.createElement('canvas');
      draw(front, frontImg, g, pxPerIn);
      var back = document.createElement('canvas');
      /* `backImg || frontImg` is the rectangle case: same art both sides. On an
         arrow the caller has already refused to get here without a back image. */
      draw(back, backImg || frontImg, mirroredGeometry(g), pxPerIn);

      cv.width = W; cv.height = H;
      var c = cv.getContext('2d');
      c.fillStyle = '#ffffff'; c.fillRect(0, 0, W, H);
      c.drawImage(front, 0, band);
      c.drawImage(back, faceW + gutter, band);

      /* Hairline, centred in the gutter: enough to say "two faces", not enough
         to be mistaken for a cut line. */
      c.fillStyle = '#121212';
      c.fillRect(faceW + Math.floor(gutter / 2), band, 1, faceH);

      c.fillStyle = '#121212';
      c.font = '600 ' + Math.round(band * 0.68) +
        'px system-ui, -apple-system, Helvetica, Arial, sans-serif';
      c.textBaseline = 'middle';
      c.textAlign = 'left';
      c.fillText('FRONT', 0, band / 2);
      c.fillText('BACK', faceW + gutter, band / 2);
    }

    /* Non-fatal. Never touches the gate. */
    function makeProof(img, backImg, g, token) {
      try {
        var scale = PROOF_LONGEST_EDGE / Math.max(g.bleedW, g.bleedH);
        var off = document.createElement('canvas');
        /* Single-sided stays ONE UNLABELLED FACE — byte-comparable to the
           order-#1022 baseline. The shape of the image is then itself the
           answer to "which product am I holding", with nothing to read. */
        if (sides === 'double') drawProofSheet(off, img, backImg, g, scale);
        else draw(off, img, g, scale);
        off.toBlob(function (blob) {
          if (!blob) { console.warn('[sign-uploader] proof toBlob returned null'); return; }
          post(blob, 'proof.jpg').then(function (r) {
            if (token === enrichToken) set('Proof', r.url);
          }).catch(function (err) {
            console.warn('[sign-uploader] proof upload failed; Proof stays empty', err);
          });
        }, 'image/jpeg', 0.85);
      } catch (err) {
        console.warn('[sign-uploader] proof render failed (tainted canvas?)', err);
      }
    }

    function renderOrientationChips() {
      var all = SC.availableOrientations(spec);
      if (all.length < 2) { orientRow.hidden = true; return; }
      orientRow.hidden = false;
      orientChips.textContent = '';
      all.forEach(function (o) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sign-uploader__chip';
        b.setAttribute('aria-pressed', o === orientation ? 'true' : 'false');
        b.textContent = o.charAt(0).toUpperCase() + o.slice(1);
        b.addEventListener('click', function () {
          if (o === orientation) return;
          orientation = o;
          reselect();
        });
        orientChips.appendChild(b);
      });
    }

    /**
     * The Points row. Renders exactly as the Orientation row does, from a
     * structural list, and for the same reason: nothing here knows that arrows
     * are the shape with a direction. `pointsSiblings` matches on `pairId`, so a
     * config with no sibling returns one option and the row stays hidden — every
     * rectangle, unchanged, with no branch to reach it.
     *
     * DIRECTION IS THE REGISTRY ID, NOT A PROPERTY. Swapping the chip swaps
     * `spec`/`specId` for the sibling row and nothing else: `set('Config',
     * specId)` in `enrich` and in the submit handler are untouched, and the
     * value they write is still a bare key of SIGN_SPECS that production can
     * look up. Building `specId + '-l'` at the write site would be a second
     * encoding of one fact in the order path, and the lookup would miss.
     *
     * Registry order is the render order, which puts Right before Left.
     */
    function renderPointsChips() {
      var all = SC.pointsSiblings(spec);
      if (all.length < 2) { pointsRow.hidden = true; return; }
      pointsRow.hidden = false;
      pointsChips.textContent = '';
      all.forEach(function (sib) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sign-uploader__chip';
        b.setAttribute('aria-pressed', sib.id === spec.id ? 'true' : 'false');
        b.textContent = sib.points === 'left' ? 'Left' : 'Right';
        b.addEventListener('click', function () {
          if (sib.id === spec.id) return;
          spec = sib;
          specId = sib.id;
          size = String(sib.label).split(' ')[0];
          /* Siblings offer the same orientations today; if one ever does not,
             fall to its default rather than throw inside resolveSign. */
          if (!SC.isOrientationAvailable(sib, orientation)) orientation = sib.defaultOrientation;
          reselect();
        });
        pointsChips.appendChild(b);
      });
    }

    /**
     * Sides, from sign-selector.js 7.2.
     *
     * The uploader has no business reading the variant selects: the selector
     * resolves the option's POSITION BY NAME, and a second copy of that logic
     * here is what its own comment says silently drives the wrong select. It
     * dispatches, this listens.
     *
     * Guarded on `designUrl()` because this fires on every selector render,
     * including the first, long before there is anything to re-proof. When a
     * design IS present a Sides change invalidates Proof and DesignState the
     * same way the chips do — a single-face proof left standing on a
     * double-sided line is a proof of the wrong object.
     */
    document.addEventListener('sign:selection', function (e) {
      var next = e && e.detail && e.detail.sides;
      if (oneFace) return;
      if (!next || next === sides) return;
      var was = sides;
      sides = next;
      /* DOUBLE -> SINGLE DROPS THE BACK CLEANLY. The object URL is revoked, the
         in-flight upload is orphaned by its token, the input is emptied and
         DesignBack is cleared — so a customer who switches back to Double is
         asked for the file again rather than silently shipping the old one, and
         a single-sided line can never carry a second print file. */
      if (was === 'double' && next !== 'double') clearBack();
      renderFaceSlots();
      applyGate();
      if (!designUrl()) return;
      set('Proof', ''); set('DesignState', '');
      enrich();
    });

    /**
     * React to a CHANGE of the Design URL, not to every mutation.
     *
     * The observer watches the whole form, and the widget mutates its own
     * receipt inside it — thumbnail, filename, the remove button. Re-running
     * enrichment on each of those would fire a fresh Proof and DesignState
     * upload per mutation, hammer the backend, and leave the winner decided by
     * whichever request happened to resolve last.
     */
    var lastDesign = null;
    function reconcile() {
      var url = designUrl();
      if (url !== lastDesign) {
        lastDesign = url;
        if (url) {
          if (!uploadEventSent) {
            uploadEventSent = true;
            var p = {
              spec: specId,                                   /* 'rect-18x24' | 'rect-12x18' | 'arrow-24x18' */
              face: 'front',
              file_type: extOf(file ? file.name : ''),        /* extension only — never the name */
              preview: hasPreview() ? 'yes' : 'no',
              sides: sides                                    /* 'single' | 'double' */
            };
            try { if (typeof window.gtag === 'function') window.gtag('event', 'sign_artwork_uploaded', p); } catch (e) {}
            try { if (typeof window.fbq === 'function') window.fbq('trackCustom', 'SignArtworkUploaded', p); } catch (e) {}
          }
          enrich();
        } else {
          enrichToken++;
          set('Size', ''); set('Orientation', ''); set('Config', ''); set('Source', '');
          clearEnrichment();
        }
      }
      /* OUTSIDE the change check, deliberately. The observer fires on every
         widget mutation, and the widget's own enable/disable is one of them —
         so a re-enable while the back is still missing is re-held here, on the
         same tick, instead of leaving the button open for the next mutation. */
      applyGate();
      syncBackPicker();
    }

    /* The widget dispatches no events (item 55): it APPENDS its Design input on
       success and REMOVES it on reset, so childList is exactly the signal. */
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () { pending = false; reconcile(); }, 0);
    }).observe(form, { childList: true, subtree: true });

    form.addEventListener('submit', function () {
      /* Re-assert the cheap, synchronous fields only; uploads must not block. */
      if (designUrl()) {
        set('Size', size); set('Orientation', orientation); set('Config', specId);
        /* NEVER ALONE: DesignBack is written only beside a Design, and only
           while this configuration actually has two faces. A stale value from a
           Sides change that raced the submit would claim a second print file on
           a single-sided line. */
        set('DesignBack', twoFace() && backUrl ? backUrl : '');
      }
    }, true);

    /* 8.6, item 123. THE SAME TWO FUNCTIONS, CALLED EARLIER — not a second
       copy of the chip markup. They decide whether each row exists (two
       orientations? a Points sibling?) and unhide it when it does, which until
       now happened only on the first enrich(), which meant only after an
       upload. The rows are no longer inside [data-preview], so what they unhide
       here stays unhidden across a remove and a replace, and the customer's
       choice survives with it. */
    renderOrientationChips();
    renderPointsChips();
    renderFaceSlots();
    renderTemplateLink();
    renderFoldoutCopy();
    reconcile();

    window.SignUploader = {
      SIGN_UPLOADER_VERSION: SIGN_UPLOADER_VERSION,
      reconcile: reconcile,
      specId: specId,
      state: function () {
        /* `specId` above is the load-time value; this one follows the Points
           row, which is the one a check on the page wants. */
        return {
          specId: specId,
          points: spec.points || null,
          orientation: orientation,
          sides: sides,
          twoFace: twoFace(),
          headerDims: headerDims,
          backHeaderDims: backHeaderDims,
          previewSource: previewSource,
          backPreviewSource: backPreviewSource,
          backUrl: backUrl,
          backPreviewUrl: backPreviewUrl,
          backError: backError,
          gateHeldByUs: gateHeldByUs,
          proofable: !!localUrl
        };
      }
    };
  }

  window.SignUploader = { SIGN_UPLOADER_VERSION: SIGN_UPLOADER_VERSION };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
